import { DesignData } from 'editor/src/store/design/types';
import { UploadedFontData } from 'editor/src/store/fonts/types';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

export interface ImportInDesignGalleryImage {
  id: string;
  name: string;
  type: string;
  width: number;
  height: number;
  state: string;
  url: string;
  thumbnailUrl: string;
}

export interface ImportInDesignWarning {
  code: string;
  message: string;
}

export interface ImportInDesignError {
  code: string;
  message: string;
}

export interface ImportInDesignSuccessResponse {
  status: 'success';
  galleryImages?: ImportInDesignGalleryImage[];
  designData: DesignData;
  warnings: ImportInDesignWarning[];
  fonts?: UploadedFontData[];
}

export interface ImportInDesignNeedsFitModeResponse {
  status: 'needs_fit_mode';
  fitOptions: string[];
  warnings: ImportInDesignWarning[];
}

export interface ImportInDesignErrorResponse {
  status: 'error';
  errors: ImportInDesignError[];
}

export type ImportInDesignResponse =
  | ImportInDesignSuccessResponse
  | ImportInDesignNeedsFitModeResponse
  | ImportInDesignErrorResponse;

interface Params {
  file: File;
  productUid: string;
  fitMode?: 'fit_inside' | 'fill_available_area';
  clientId?: string;
}

const pendingRequests = new Map<string, (response: ImportInDesignResponse) => void>();

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `indesign-import-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function resolveImportInDesignRequest(requestId: string, response: ImportInDesignResponse): void {
  const resolver = pendingRequests.get(requestId);
  if (!resolver) {
    return;
  }
  pendingRequests.delete(requestId);
  resolver(response);
}

function importInDesignApi({ file, productUid, fitMode, clientId }: Params): Promise<ImportInDesignResponse> {
  const requestId = generateRequestId();

  return new Promise<ImportInDesignResponse>((resolve) => {
    pendingRequests.set(requestId, resolve);
    sendPostMessage('indesign.importRequested', {
      requestId,
      file,
      productUid,
      fitMode,
      clientId,
    });
  });
}

export default importInDesignApi;
